<template>
    <div class="report-content">
        <div class="detailing">
            <Hide v-model="staffActivityVisibility">
                <h4>{{ $t('reports.staffPerformance.staffActivity') }}</h4>
            </Hide>
        </div>
        <div v-show="staffActivityVisibility">
            <table
                v-if="!staffActivityLoader && staffActivity.length > 0"
                id="forAT_satable-StaffPerformanceReport"
                class="common-table"
            >
                <tr>
                    <th>{{ $t('reports.staffPerformance.activityTable.activity') }}</th>
                    <th>{{ $t('reports.staffPerformance.activityTable.numberOfCalls') }}</th>
                    <th>{{ $t('reports.staffPerformance.activityTable.notOnTime') }}</th>
                    <th>{{ $t('reports.staffPerformance.activityTable.late') }}</th>
                    <th>{{ $t('reports.staffPerformance.activityTable.avResponseTime') }}</th>
                    <th>{{ $t('reports.staffPerformance.activityTable.avDurationTime') }}</th>
                </tr>
                <tbody>
                    <tr v-for="(action, index) in staffActivity" :key="index">
                        <td>{{ $t('reports.staffPerformance.' + action.action.toLowerCase()) }}</td>
                        <td>{{ action.count }}</td>
                        <td>{{ action.not_on_time }}</td>
                        <td>{{ action.late }}</td>
                        <td>{{ action.avgResponseTimeFormatted }}</td>
                        <td>{{ action.avgDurationTimeFormatted }}</td>
                    </tr>
                </tbody>
            </table>

            <LoadingBlock v-if="staffActivityLoader" />
            <h4 v-if="!staffActivityLoader && staffActivity.length === 0" class="data-empty">
                {{ $t('common.noData') }}
            </h4>
        </div>

        <div class="graphics">
            <Hide v-model="chartVisibility">
                <h4>{{ $t('reports.staffPerformance.performanceStatistics') }}</h4>
            </Hide>

            <div v-show="chartVisibility">
                <LoadingBlock v-if="staffActivityLoader" />
                <h4 v-if="!staffActivityLoader && staffActivity.length === 0" class="data-empty">
                    {{ $t('common.noData') }}
                </h4>
                <div v-show="!staffActivityLoader && staffActivity.length > 0" class="pie-chart-wrapper">
                    <div class="sp-numberof-calls ct-chart ct-major-tenth"></div>
                </div>
                <div v-show="!staffActivityLoader && staffActivity.length > 0" class="pie-chart-wrapper">
                    <div class="sp-ontime ct-chart ct-major-tenth"></div>
                </div>
            </div>
        </div>

        <div class="detailing">
            <Hide v-model="callsDetailingVisibility">
                <h4 ref="details">{{ $t('reports.staffPerformance.callsDetailing') }}</h4>
            </Hide>
        </div>
        <div v-show="callsDetailingVisibility">
            <Pagination
                v-if="staffLength > pagination.per_page"
                v-model="pagination.current_page"
                :options="pagination"
                :callback="handlePaginationCallback"
                :to-cache-pages="toCachePages"
            />
            <table
                v-if="!tableLoader && reportData.length > 0"
                id="forAT_cdtable-StaffPerformanceReport"
                class="common-table"
            >
                <tr>
                    <th>{{ $t('reporting.commonLabels.callType') }}</th>
                    <th>{{ $t('reports.staffPerformance.callsTable.location') }}</th>
                    <th>{{ $t('reporting.commonLabels.startTimeDate') }}</th>
                    <th>{{ $t('reports.staffPerformance.activityTable.activity') }}</th>
                    <th>{{ $t('reports.staffPerformance.callsTable.responseTime') }}</th>
                    <th>{{ $t('reports.staffPerformance.callsTable.durationTime') }}</th>
                </tr>
                <tbody>
                    <tr v-for="(action, index) in reportData" :key="index">
                        <td>
                            <div class="call-type">
                                {{ action.type[0] }}
                                <span>
                                    <i
                                        v-for="(type, index2) in parseCallTypes(action.categories)"
                                        :key="index2"
                                        :class="'icon-' + type.toLocaleLowerCase()"
                                    ></i>
                                </span>
                            </div>
                        </td>
                        <td class="location">{{ action.location }}</td>
                        <td>
                            <div class="date">{{ action.startDateFormatted }}</div>
                            <div class="date">{{ action.startTimeFormatted }}</div>
                        </td>
                        <td>{{ action.activity }}</td>
                        <td>{{ action.responseTimeFormatted }}</td>
                        <td>{{ action.durationTimeFormatted }}</td>
                    </tr>
                </tbody>
            </table>
            <LoadingBlock v-if="tableLoader" />
            <Pagination
                v-if="staffLength > pagination.per_page"
                v-model="pagination.current_page"
                :options="pagination"
                :callback="handlePaginationCallback"
                :to-cache-pages="toCachePages"
            />

            <h4 v-if="!tableLoader && reportData.length === 0" class="data-empty">
                {{ $t('common.noData') }}
            </h4>
        </div>
    </div>
</template>

<script type="text/babel">
import Hide from '../../common/Hide.vue';
import min from 'lodash-es/min';
import getTime from 'date-fns/getTime';
import parseISO from 'date-fns/parseISO';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import Chartist from 'ascom-chartist-logscale';
import {
    resourceStaffPerformanceReport,
    resourceActivityStatistics,
    resourceStaffPerformanceLength,
} from '../../../api/resources';
import { getCancelToken } from '../../../api';
import reportingUtils from '../../common/reportingUtils';
import LoadingBlock from '../../common/LoadingBlock';
import Pagination from '../../common/Pagination.vue';
import { mapState, mapActions } from 'vuex';
import {
    formatDateString,
    getTimeString,
    doScroll,
    secsToReadableTime,
    getDateFormat,
} from '../../common/helpers';
import { flipThePage, toCachePages } from '../helpers/reportsHelper';
import isEqual from 'lodash-es/isEqual';
import { ERROR } from '../../../constants';

export default {
    name: 'StaffPerformance',

    components: {
        Pagination,
        Hide,
        LoadingBlock,
    },

    mixins: [reportingUtils],

    props: {
        reportParameters: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            staffActivity: [],
            reportData: [],
            staffLength: 0,
            pagination: {
                current_page: 1,
                last_page: 0,
                per_page: 25,
            },
            cache: {},
            staffActivityLoader: true,
            tableLoader: true,
            staffActivityVisibility: true,
            callsDetailingVisibility: true,
            chartVisibility: true,
            cancelToken: getCancelToken(),
            cancelPaginationToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState(['callTypes']),

        /**
         * Report parameters with targets for call catagories.
         * @returns {object}
         */
        params() {
            return {
                ...this.reportParameters,
                targets: this.callTypes.map(({ time_target_enabled, delayed, till, id, target }) => ({
                    delayed,
                    time_target_enabled,
                    till,
                    target,
                    type: id,
                })),
            };
        },
    },

    watch: {
        reportParameters(newParameters, oldParameters) {
            if (!isEqual(newParameters, oldParameters)) {
                this.resetToDefault();
                this.loadReport();
            }
        },

        'pagination.current_page'() {
            doScroll(this.$refs.details);
        },

        chartVisibility(value) {
            if (value) {
                if (this.staffActivity.length > 0) {
                    this.numberOfCallsChart();
                    this.onTimeAndNotOnTimeChart();
                }
            }
        },
    },

    mounted() {
        this.loadReport();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
        this.cancelPaginationToken.cancel();
    },

    methods: {
        toCachePages,

        ...mapActions({
            popup: 'popup/popup',
        }),

        /**
         * Load report data. In case of error will be displayed popup message with text.
         */
        async loadReport() {
            try {
                await Promise.all([this.loadStaffActivity(), this.loadStaffLengthAndTableData()]);
            } catch (error) {
                this.staffActivityLoader = false;
                this.tableLoader = false;
                if (error !== 'canceled' && error.data && error.data.error) {
                    this.popup({
                        type: ERROR,
                        header: this.$t('common.error'),
                        text: this.$t(error.data.error),
                    });
                }
            }
        },

        /**
         * Load data for Staff activity statistics table and Performance statistics charts.
         */
        async loadStaffActivity() {
            let data = (
                await resourceActivityStatistics({
                    data: this.params,
                    cancelToken: this.cancelToken.token,
                })
            ).data;

            let newData;
            if (data.length > 0 && data.length < 3) {
                newData = this.insertEmptyAction(data);
            } else {
                newData = data ? data : [];
            }

            this.staffActivity = newData.map(action => ({
                ...action,
                avgResponseTimeFormatted: secsToReadableTime(action.avg_response_time),
                avgDurationTimeFormatted: secsToReadableTime(action.avg_duration_time),
            }));

            this.staffActivityLoader = false;
            if (this.staffActivity.length > 0) {
                this.numberOfCallsChart();
                this.onTimeAndNotOnTimeChart();
            }
        },

        /**
         * Fills in spaces in the table, if any actions are not present.
         */
        insertEmptyAction(data) {
            let template = {
                count: 0,
                not_on_time: 0,
                late: 0,
                avg_response_time: 0,
                avg_duration_time: 0,
            };

            let newData = [];
            ['ACCEPT', 'MISSED', 'REJECT'].forEach(action => {
                newData.push(
                    !data.some(el => el.action === action)
                        ? { ...template, action }
                        : data.find(el => el.action === action)
                );
            });
            return newData;
        },

        /**
         * Getting count of records in the table. If value > 0 then will be initialized loadTableData function.
         */
        async loadStaffLengthAndTableData() {
            this.staffLength = (
                await resourceStaffPerformanceLength({
                    data: this.params,
                    cancelToken: this.cancelToken.token,
                })
            ).data;

            if (this.staffLength) {
                this.pagination.last_page = Math.ceil(this.staffLength / this.pagination.per_page);
                this.loadTableData();
            } else {
                this.tableLoader = false;
            }
        },

        /**
         * Init number of calls chart
         */
        numberOfCallsChart() {
            let series = [
                {
                    value: this.staffActivity[1].count,
                    meta: this.$t('reports.staffPerformance.activityTable.missedCalls'),
                    className: 'ct-missed-bar',
                },
                {
                    value: this.staffActivity[2].count,
                    meta: this.$t('reports.staffPerformance.activityTable.callsRejected'),
                    className: 'ct-rejected-bar',
                },
                {
                    value: this.staffActivity[0].count,
                    meta: this.$t('reports.staffPerformance.activityTable.callsAccepted'),
                    className: 'ct-accepted-bar',
                },
            ];

            let data = { series };

            let options = this.pieConfiguration(series);

            new Chartist.Pie('.sp-numberof-calls', data, options);
        },

        /**
         * Calculate values and init chart for On time and Not on time values.
         */
        onTimeAndNotOnTimeChart() {
            let series = [
                {
                    value: this.staffActivity[1].count - this.staffActivity[1].not_on_time,
                    meta:
                        this.$t('reports.staffPerformance.activityTable.missedCalls') +
                        ' ' +
                        this.$t('reports.staffPerformance.onTime'),
                    className: 'ct-missed-ontime-bar',
                },
                {
                    value: this.staffActivity[2].count - this.staffActivity[2].not_on_time,
                    meta:
                        this.$t('reports.staffPerformance.activityTable.callsRejected') +
                        ' ' +
                        this.$t('reports.staffPerformance.onTime'),
                    className: 'ct-rejected-ontime-bar',
                },
                {
                    value: this.staffActivity[0].count - this.staffActivity[0].not_on_time,
                    meta:
                        this.$t('reports.staffPerformance.activityTable.callsAccepted') +
                        ' ' +
                        this.$t('reports.staffPerformance.onTime'),
                    className: 'ct-accepted-ontime-bar',
                },
                {
                    value: this.staffActivity[1].not_on_time,
                    meta:
                        this.$t('reports.staffPerformance.activityTable.missedCalls') +
                        ' ' +
                        this.$t('reports.staffPerformance.notOnTime'),
                    className: 'ct-missed-notontime-bar',
                },
                {
                    value: this.staffActivity[2].not_on_time,
                    meta:
                        this.$t('reports.staffPerformance.activityTable.callsRejected') +
                        ' ' +
                        this.$t('reports.staffPerformance.notOnTime'),
                    className: 'ct-rejected-notontime-bar',
                },
                {
                    value: this.staffActivity[0].not_on_time,
                    meta:
                        this.$t('reports.staffPerformance.activityTable.callsAccepted') +
                        ' ' +
                        this.$t('reports.staffPerformance.notOnTime'),
                    className: 'ct-accepted-notontime-bar',
                },
            ];

            let data = { series };

            let options = this.pieConfiguration(series);

            new Chartist.Pie('.sp-ontime', data, options);
        },

        handlePaginationCallback() {
            this.tableLoader = false;
            flipThePage.call(this);
        },

        /**
         * Load and prepare data for tables.
         * @returns {Promise}
         */
        loadTableData() {
            this.tableLoader = true;
            let data = {
                ...this.reportParameters,
                limit: 25,
                offset: (this.pagination.current_page - 1) * this.pagination.per_page,
            };

            return resourceStaffPerformanceReport({ data, cancelToken: this.cancelPaginationToken.token })
                .then(({ data }) => {
                    this.reportData = data.map(activity => this.setDataForCallsDetailing(activity));
                    if (this.reportData.length > 0) {
                        this.cache[this.pagination.current_page] = this.reportData;
                    }
                })
                .catch(response => {
                    // TODO Handler error (https://jira.ascom-ws.com/browse/MERA-1546)
                    return response;
                })
                .then(response => {
                    if (response !== 'canceled') {
                        this.tableLoader = false;
                    }
                });
        },

        pieConfiguration(series) {
            return {
                donut: true,
                showLabel: false,
                donutWidth: 45,
                plugins: [
                    Chartist.plugins.tooltip(),
                    Chartist.plugins.legend({
                        legendNames: series.map(serie => serie.meta),
                        position: 'top',
                        clickable: false,
                        classNames: series.map(serie => serie.className),
                    }),
                ],
            };
        },

        /**
         * Returns array with response and duration time
         * @param {object} activity
         * @param {string|null} cacheAccepted
         * @param {string|null} cacheNp
         * @param {string|null} catChanged
         * @return {[*,*]}
         */
        getResponseAndDurationTime(activity, cacheAccepted, cacheNp, catChanged) {
            let response = this.getResponseTime(
                activity.start_time,
                activity.end_time,
                catChanged,
                cacheAccepted,
                cacheNp
            );
            let duration = this.timeDiff(activity.start_time, activity.end_time);

            return [response, duration];
        },

        /**
         * Calculate response time for action, without relying on call.till
         * @param {string} start
         * @param {string|null} end
         * @param {string|null} catChanged
         * @param {string|null} cacheAccepted
         * @param {string|null} cacheNp
         * @return {number}
         */
        getResponseTime(start, end, catChanged, cacheAccepted, cacheNp) {
            let response;
            if (
                !!catChanged &&
                (getTime(parseISO(cacheAccepted)) > getTime(parseISO(catChanged)) ||
                    getTime(parseISO(cacheNp)) > getTime(parseISO(catChanged)))
            ) {
                response = this.timeDiff(start, end);
            } else {
                response = this.timeDiff(start, min([cacheNp, cacheAccepted]));
            }

            return response;
        },

        /**
         * Get difference between two dates in seconds
         * @param start
         * @param {string} end
         * @return {number}
         */
        timeDiff(start, end) {
            return differenceInSeconds(parseISO(end), parseISO(start));
        },

        /**
         * Prepare data calls detailing table
         * @param {object} activity
         */
        setDataForCallsDetailing(activity) {
            let cacheAccepted = activity.cache_accepted || activity.end_time;
            let cacheNp = activity.cache_np || activity.end_time;
            let catChanged = activity.cache_cat_changed;

            let responseDuration = this.getResponseAndDurationTime(
                activity,
                cacheAccepted,
                cacheNp,
                catChanged
            );

            return {
                categories: activity.categories,
                type: this.parseCallTypes(activity.categories),
                location: activity.location,
                start_time: activity.start_time,
                startDateFormatted: formatDateString(activity.start_time, getDateFormat()),
                startTimeFormatted: getTimeString(activity.start_time),
                activity: this.$t('reports.staffPerformance.callsTable.' + activity.action),
                responseTime: responseDuration[0],
                responseTimeFormatted: secsToReadableTime(responseDuration[0]),
                durationTime: responseDuration[1],
                durationTimeFormatted: secsToReadableTime(responseDuration[1]),
            };
        },

        resetToDefault() {
            this.staffActivity = [];
            this.reportData = [];
            this.staffLength = 0;
            this.pagination = {
                current_page: 1,
                last_page: 0,
                per_page: 25,
            };
            this.cache = {};
            this.staffActivityLoader = true;
            this.tableLoader = true;
        },
    },
};
</script>
